/**
 * Uso real da conversa ao vivo, somado fala por fala.
 *
 * O Gemini Live cobra, a cada fala, o contexto inteiro (instruções, ferramentas e a
 * conversa até ali) e manda o uso daquela fala em `usageMetadata`. Guardar só o
 * último retrato subestimava a conversa em várias vezes. Dentro de uma fala a
 * entrada (`promptTokenCount`) não muda e só a saída cresce; entrada diferente é
 * fala nova. Então: mesma entrada substitui o retrato da fala em curso; entrada
 * nova fecha a fala anterior e soma.
 */
type Detail = { modality?: string; tokenCount?: number };
type Snapshot = {
  promptTokenCount?: number;
  responseTokenCount?: number;
  thoughtsTokenCount?: number;
  promptTokensDetails?: Detail[];
  responseTokensDetails?: Detail[];
};

export type SummedUsage = {
  /** Marca para o servidor: soma de todas as falas (apps antigos mandam só o último retrato). */
  summed: true;
  turns: number;
  promptTokenCount: number;
  responseTokenCount: number;
  thoughtsTokenCount: number;
  promptTokensDetails: { modality: string; tokenCount: number }[];
  responseTokensDetails: { modality: string; tokenCount: number }[];
};

const num = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

function add(into: Map<string, number>, details: Detail[] | undefined) {
  for (const item of details ?? []) {
    const key = String(item.modality ?? 'TEXT');
    into.set(key, (into.get(key) ?? 0) + num(item.tokenCount));
  }
}

export class UsageMeter {
  private current: Snapshot | null = null;
  private turns = 0;
  private prompt = 0;
  private response = 0;
  private thoughts = 0;
  private promptBy = new Map<string, number>();
  private responseBy = new Map<string, number>();

  /** Um retrato de uso chegou do provedor. */
  record(snapshot: Record<string, unknown>): void {
    const next = snapshot as Snapshot;
    if (this.current && num(this.current.promptTokenCount) === num(next.promptTokenCount)) {
      this.current = next;
      return;
    }
    this.commit();
    this.current = next;
  }

  private commit(): void {
    const turn = this.current;
    if (!turn) return;
    this.turns += 1;
    this.prompt += num(turn.promptTokenCount);
    this.response += num(turn.responseTokenCount);
    this.thoughts += num(turn.thoughtsTokenCount);
    add(this.promptBy, turn.promptTokensDetails);
    add(this.responseBy, turn.responseTokensDetails);
    this.current = null;
  }

  /** O total até agora (inclui a fala em curso, sem fechá-la), ou nada se ainda não houve uso. */
  total(): SummedUsage | null {
    const turn = this.current;
    const turns = this.turns + (turn ? 1 : 0);
    if (turns === 0) return null;
    const promptBy = new Map(this.promptBy);
    const responseBy = new Map(this.responseBy);
    add(promptBy, turn?.promptTokensDetails);
    add(responseBy, turn?.responseTokensDetails);
    const list = (map: Map<string, number>) =>
      [...map.entries()].map(([modality, tokenCount]) => ({ modality, tokenCount }));
    return {
      summed: true,
      turns,
      promptTokenCount: this.prompt + num(turn?.promptTokenCount),
      responseTokenCount: this.response + num(turn?.responseTokenCount),
      thoughtsTokenCount: this.thoughts + num(turn?.thoughtsTokenCount),
      promptTokensDetails: list(promptBy),
      responseTokensDetails: list(responseBy),
    };
  }
}
